import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Box, Typography, Autocomplete, CircularProgress } from '@mui/material';
import ReportService from '../../../api/services/reportService';

export default function ScheduleReportModal({ open, onClose, tenantId, onSuccess }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    
    const [reportType, setReportType] = useState('Occupancy');
    const [frequency, setFrequency] = useState('Daily');
    const [deliveryTime, setDeliveryTime] = useState('08:00');
    const [recipients, setRecipients] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [format, setFormat] = useState('PDF');

    // Basic email validation regex
    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleClose = () => {
        setReportType('Occupancy');
        setFrequency('Daily');
        setDeliveryTime('08:00');
        setRecipients([]);
        setInputValue('');
        setFormat('PDF');
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        setError('');
        
        let finalRecipients = [...recipients];
        if (inputValue.trim()) {
            finalRecipients.push(inputValue.trim());
        }
        
        if (finalRecipients.length === 0) {
            setError('At least one recipient email is required.');
            return;
        }

        const invalidEmails = finalRecipients.filter(email => !isValidEmail(email));
        if (invalidEmails.length > 0) {
            setError(`Invalid email format: ${invalidEmails.join(', ')}`);
            return;
        }

        if (!deliveryTime) {
            setError('Delivery time is required.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                reportType,
                frequency,
                deliveryTime,
                recipients: finalRecipients,
                format
            };
            
            await ReportService.scheduleReport(tenantId, payload);
            
            onSuccess('Automated reporting route successfully established and scheduled');
            handleClose();
        } catch (err) {
            console.error('Error scheduling report:', err);
            setError(err.response?.data?.message || 'Failed to schedule report. Please check server connection.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSubmitting ? undefined : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>Configure Automated Report</DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'error.main', color: 'white', borderRadius: 1 }}>
                        <Typography variant="body2">{error}</Typography>
                    </Box>
                )}
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Report Type</InputLabel>
                        <Select
                            value={reportType}
                            label="Report Type"
                            onChange={(e) => setReportType(e.target.value)}
                            disabled={isSubmitting}
                        >
                            <MenuItem value="Occupancy">Occupancy</MenuItem>
                            <MenuItem value="Revenue">Revenue</MenuItem>
                            <MenuItem value="Arrivals-Departures">Arrivals-Departures</MenuItem>
                            <MenuItem value="P&L">P&L</MenuItem>
                            <MenuItem value="Inventory">Inventory</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel>Execution Frequency</InputLabel>
                        <Select
                            value={frequency}
                            label="Execution Frequency"
                            onChange={(e) => setFrequency(e.target.value)}
                            disabled={isSubmitting}
                        >
                            <MenuItem value="Daily">Daily</MenuItem>
                            <MenuItem value="Weekly">Weekly</MenuItem>
                            <MenuItem value="Monthly">Monthly</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Target Delivery Time"
                        type="time"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }} // 5 min
                        value={deliveryTime}
                        onChange={(e) => setDeliveryTime(e.target.value)}
                        disabled={isSubmitting}
                    />

                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={recipients}
                        onChange={(event, newValue) => {
                            setRecipients(newValue);
                        }}
                        inputValue={inputValue}
                        onInputChange={(event, newInputValue) => {
                            setInputValue(newInputValue);
                        }}
                        disabled={isSubmitting}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const isValid = isValidEmail(option);
                                return (
                                    <Chip
                                        key={index}
                                        label={option}
                                        {...getTagProps({ index })}
                                        size="small"
                                        color={isValid ? "primary" : "error"}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                label="Recipients (Emails)"
                                placeholder="Press enter to add..."
                                helperText="Type an email and press Enter"
                            />
                        )}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel>File Format</InputLabel>
                        <Select
                            value={format}
                            label="File Format"
                            onChange={(e) => setFormat(e.target.value)}
                            disabled={isSubmitting}
                        >
                            <MenuItem value="PDF">PDF</MenuItem>
                            <MenuItem value="CSV">CSV</MenuItem>
                            <MenuItem value="Excel">Excel</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSubmit} 
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Scheduling...' : 'Schedule Report'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
